import React from 'react';
import { TableCell, TableRow } from '@mui/material';
import type { UsageApiOperationRow } from '../types';
import { LEER, formatCount, formatDuration, formatPercent, topResponseCode } from '../services/usageFormat';
import SchutzZeile from './SchutzZeile';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';
import { nutzerText } from './SeitenTabelle';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — Serveroperationen mit Fehlerquote und Laufzeiten.
 *
 * Erfasst sind nur Name, Art und Dauer, nie Parameter. Die Spalte heißt „Häufigster Antwortcode“
 * und nicht „Fehlercode“: gezählt wird jeder Antwortcode, auch der einer erfolgreichen Antwort —
 * die Zahl der Fehler steht getrennt in der Fehlerquote.
 */
export interface ServeroperationenTabelleProps {
  rows: UsageApiOperationRow[];
}

const perzentilText = (ms: number | null, note: string | null): string => {
  if (ms !== null) return formatDuration(ms);
  return note ?? LEER;
};

export default function ServeroperationenTabelle({ rows }: ServeroperationenTabelleProps) {
  return (
    <DatenTabelle
      leer={rows.length === 0}
      leerText="Im Zeitraum sind keine Serveroperationen verdichtet."
      spalten={[
        { titel: 'Operation' },
        { titel: 'Art' },
        { titel: 'Aufrufe', numerisch: true },
        { titel: 'Nutzer', numerisch: true },
        { titel: 'Fehlerquote', numerisch: true },
        { titel: 'Median', numerisch: true },
        { titel: '95 %', numerisch: true },
        { titel: 'Häufigster Antwortcode' },
      ]}
    >
      {rows.map((row, stelle) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRow key={`${row.label}-${stelle}`}>
          <TableCell>
            <SchutzZeile
              suppressed={row.suppressed}
              groups={row.groups}
              users={row.users}
              bezeichner={row.name}
              technisch
            />
          </TableCell>
          <TableCell>{row.suppressed ? '–' : (row.kind ?? LEER)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatCount(row.count)}</TableCell>
          <TableCell sx={zahlenZelle}>{nutzerText(row.users, row.usersEstimated)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatPercent(row.errorRate)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatDuration(row.p50Ms)}</TableCell>
          <TableCell sx={zahlenZelle}>{perzentilText(row.p95Ms, row.p95Note)}</TableCell>
          {/* Sammelzeilen tragen kein topCodes (usage.graphql: dort immer null) — die leere
              Zeichenkette wird deshalb zum Strich, nicht zu einem geratenen Code. */}
          <TableCell>{topResponseCode(row.topCodes) || LEER}</TableCell>
        </TableRow>
      ))}
    </DatenTabelle>
  );
}
